import { fetchWithContext } from "../../../utils/fetch";

/**
 * Client and parser for the connect instructions
 * (GET /api/cluster/{name}/login-steps), which V2 surfaces behind its CONNECT control.
 *
 * The endpoint returns a per-cloud runbook for reaching a customer's cluster: VPN reminder,
 * how to get onto the debugger pod, an export block of temporary credentials, and the
 * kubeconfig command. It is guarded by globalBypass or support operational access, so it is
 * Orkes staff only.
 *
 * **The response body contains live credentials.** For AWS it mints temporary STS keys through
 * AssumeRole at request time and embeds them in the export block. Everything downstream of this
 * module should treat the payload as a secret: do not log it, do not put it in an error message,
 * and do not cache it anywhere that outlives the dialog.
 */

export interface LoginSteps {
  clusterName?: string;
  accountId?: string;
  region?: string;
  isOrkesHosted?: boolean;
  instructions?: string[];
}

export async function fetchLoginSteps(
  context: object,
  headers: object,
  clusterName: string,
  organizationId?: string
): Promise<LoginSteps> {
  const params = new URLSearchParams();
  if (organizationId) {
    params.set("maybeOrganizationId", organizationId);
  }
  const query = params.toString();
  return fetchWithContext(
    `/cluster/${encodeURIComponent(clusterName)}/login-steps${
      query ? `?${query}` : ""
    }`,
    context,
    { headers }
  );
}

export type ConnectBlock =
  | { kind: "text"; text: string }
  | { kind: "code"; code: string };

/** A line that is only a fence, whatever indentation the server happened to emit. */
function isFence(line: string): boolean {
  return line.trim() === "```";
}

/**
 * Turns the server's flat array of lines into renderable blocks.
 *
 * The shape is awkward and worth describing, because it is not one line per array entry. Fences
 * arrive as their own entries with arbitrary leading whitespace, a single entry can carry several
 * newline-separated commands, and blank padding entries appear between them. So the parser toggles
 * on fences and joins whatever falls between, rather than trying to interpret entries
 * individually.
 */
export function parseInstructions(instructions?: string[]): ConnectBlock[] {
  const blocks: ConnectBlock[] = [];
  let inCode = false;
  let buffer: string[] = [];

  const flush = () => {
    const joined = buffer.join("\n");
    // Trailing blank padding is common between a fence and the next step; drop it rather than
    // rendering an empty paragraph or an empty code box.
    const trimmed = inCode ? joined.replace(/^\n+|\s+$/g, "") : joined.trim();
    if (trimmed.length > 0) {
      blocks.push(
        inCode
          ? { kind: "code", code: trimmed }
          : { kind: "text", text: trimmed }
      );
    }
    buffer = [];
  };

  for (const line of instructions ?? []) {
    if (isFence(line)) {
      flush();
      inCode = !inCode;
      continue;
    }
    buffer.push(inCode ? line : line.trim());
  }
  // An unclosed fence should still render what it holds rather than swallowing it.
  flush();

  return blocks;
}

/**
 * True when a code block is the credentials export.
 *
 * Used to mark that block in the UI. Matching on the variable names rather than on the values
 * keeps this from ever needing to look at a secret.
 */
export function isCredentialBlock(block: ConnectBlock): boolean {
  return (
    block.kind === "code" &&
    /\bAWS_(ACCESS_KEY_ID|SECRET_ACCESS_KEY|SESSION_TOKEN)\b/.test(block.code)
  );
}

/**
 * The server degrades to a comment block rather than failing when it cannot mint credentials, so
 * a response can be "successful" and still contain nothing usable. Detecting that lets the dialog
 * say so instead of showing an error comment as though it were a command to run.
 */
export function isErrorBlock(block: ConnectBlock): boolean {
  return block.kind === "code" && /^#\s*Error:/m.test(block.code);
}
